import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  KeyIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowDownTrayIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/solid";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useContext, useEffect, useState } from "react";
import ReactGA from "react-ga4";
import Plot from "react-plotly.js";
import { Link, Navigate, useNavigate } from "react-router-dom";

import UsageDetailCard from "./HubUsageDetail";
import RefillSettingsCard, {
  ConvertToSubscriptionMetadata,
} from "./RefillSettingsForm";
import { SubscriptionMetadata } from "./RefillSettingsForm";
import RouteInsights from "./RouteInsights";

import { useApiKey } from "context/ApiKeyContext";
import UserContext from "context/UserContext";

import Badge from "components/atoms/Badge";
import Button from "components/atoms/Button";
import Card from "components/atoms/Card";
import Spinner from "components/atoms/Spinner";
import { convertToNumLetter } from "components/molecules/PriceTables/PriceTableNutritionAPI";
import SlackSignUp from "components/molecules/SlackSignUp";

import { error } from "utils";
import { NUTRITION_PLATFORM_SLUG } from "utils/products";
import {
  CREDIT_REFILL_PRICE_DISPLAY,
  ProductDataType,
  UsageHistoryReportForPeriod,
} from "utils/types";

type DetailsTab = "account" | "easyai" | "insights";
const SHOW_HUB_ADVANCED_TABS = false;

type EasyAISSOResponse = {
  ssoEnabled?: boolean;
  ssoUrl?: string;
  loginUrl?: string;
  expiresAt?: number;
};

const detailsTabToHash = (tab: DetailsTab): string => {
  switch (tab) {
    case "easyai":
      return "easy-ai";
    case "insights":
      return "insights";
    default:
      return "account";
  }
};

const getRequestedDetailsTab = (): DetailsTab => {
  if (!SHOW_HUB_ADVANCED_TABS) {
    return "account";
  }
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("tab");
  const fromHash = window.location.hash.replace(/^#/, "");
  const normalized = String(fromHash || fromQuery || "account")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

  if (normalized === "easyai" || normalized === "easy") return "easyai";
  if (normalized === "insights" || normalized === "insight") return "insights";
  return "account";
};

const getUsageQuota = (usageInfo: any): number =>
  Math.max(0, Number(usageInfo?.unitsQuota ?? usageInfo?.unitQuota ?? 0) || 0);

const DetailsPassioHub = () => {
  const { setApiKey } = useApiKey();
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const productQuery = params.get("product");

  const [unauthorized, setUnauthorized] = useState(false);
  const [hubSubscriptionUnavailable, setHubSubscriptionUnavailable] =
    useState(false);

  const [isCopied, setIsCopied] = useState(false);
  const [hideKey, setHideKey] = useState(true);
  const [productState, setProductState] = useState<ProductDataType>({
    isSubscribed: false,
    trialEnd: null,
    billingDate: new Date(),
    licenseKey: null,
    usageInfo: null,
    tiers: [],
  });

  // Store the API key in context whenever it changes
  useEffect(() => {
    if (productState?.licenseKey) {
      setApiKey(productState.licenseKey);
    }
  }, [productState?.licenseKey, setApiKey]);

  // TODO: Type incoming
  const [productSubDetail, setProductSubDetail] = useState<any>(null);

  const [predictedPercentage, setPredictedPercentage] = useState<number>(0);

  const calculatePredictedUsage = (
    startDate: number,
    endDate: number,
    currentUsage: number
  ): number => {
    const now = new Date();
    const start = new Date(startDate * 1000);
    const end = new Date(endDate * 1000);

    const totalDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    let daysPassed = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

    // Ensure at least 1 day has passed for the calculation, to avoid division by zero or very small numbers
    daysPassed = Math.max(daysPassed, 1);

    if (daysPassed <= 0) return currentUsage;

    const dailyRate = currentUsage / daysPassed;
    const predictedTotal = dailyRate * totalDays;

    return predictedTotal;
  };

  useEffect(() => {
    if (productSubDetail?.stripeSubscription && productSubDetail.usageInfo) {
      const predicted = calculatePredictedUsage(
        productSubDetail.stripeSubscription.current_period_start,
        productSubDetail.stripeSubscription.current_period_end,
        productSubDetail.usageInfo.units
      );
      const usageQuota = getUsageQuota(productSubDetail.usageInfo);
      setPredictedPercentage(
        usageQuota > 0 ? (predicted / usageQuota) * 100 : 0
      );
    }
  }, [productSubDetail]);

  const groupModes = [
    ["Request Type", "type"],
    ["Request Path", "path"],
    ["AI Mode", "aiMode"],
  ];
  const [usageDetailGroupMode, setUsageDetailGroupMode] = useState(
    groupModes[0]
  );
  const [usageView, setUsageView] = useState<"basic" | "graph" | "history">(
    "basic"
  );
  const [usageHistoryForPeriod, setUsageHistoryForPeriod] =
    useState<UsageHistoryReportForPeriod | null>();
  const [historyOutputMode, setHistoryOutputMode] = useState<"grouped" | "raw">(
    "grouped"
  );
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailsTab>(() =>
    getRequestedDetailsTab()
  );
  const [showEasyAISignIn, setShowEasyAISignIn] = useState(true);
  const [isActivatingEasyAI, setIsActivatingEasyAI] = useState(false);
  const [easyAIActivationMessage, setEasyAIActivationMessage] = useState("");
  const [easyAIAutoSignInTriggered, setEasyAIAutoSignInTriggered] =
    useState(false);

  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const [usageDetail, setUsageDetail] = useState<any>(null);
  const easyAIOwnerEmail = userContext?.email ?? "";
  const effectiveUsageQuota = Math.max(
    getUsageQuota(productSubDetail?.usageInfo),
    getUsageQuota(productState?.usageInfo)
  );
  const refillPricePerM =
    Number(CREDIT_REFILL_PRICE_DISPLAY.replace(/[^0-9.]/g, "")) || 2.5;
  const currentUsageTokens = Math.max(
    0,
    Number(productSubDetail?.usageInfo?.units || 0)
  );
  const projectedPeriodUsageTokens = Math.max(
    0,
    (predictedPercentage * effectiveUsageQuota) / 100
  );
  const isEasyAIEligiblePlan = effectiveUsageQuota >= 300;
  const insightsMinQuantity = 25;
  const modelOverridesMinQuantity = 25;
  const aiInsightsMinQuantity = 100;
  const subscriptionQuantity =
    productSubDetail?.stripeSubscription?.items?.data?.[0]?.quantity ??
    productSubDetail?.stripeSubscription?.items?.[0]?.quantity ??
    productSubDetail?.stripeSubscription?.quantity ??
    0;
  const hubMetadata = productSubDetail?.stripeSubscription?.metadata || {};
  const metadataRefilledTokens = Math.max(
    0,
    (parseFloat(String(hubMetadata?.refilledAmount ?? "0")) || 0) * 1000000
  );
  const baseIncludedTokens = Math.max(
    0,
    Number(subscriptionQuantity || 0) * 1000000
  );
  const metadataAllowanceTokens = baseIncludedTokens + metadataRefilledTokens;
  const totalAllowanceTokens = Math.max(
    0,
    effectiveUsageQuota,
    metadataAllowanceTokens
  );
  const includedAllowanceTokens = Math.min(
    baseIncludedTokens,
    totalAllowanceTokens
  );
  const refillAllowanceTokens = Math.max(
    0,
    totalAllowanceTokens - includedAllowanceTokens
  );
  const projectedPeriodOverageTokens = Math.max(
    0,
    projectedPeriodUsageTokens - totalAllowanceTokens
  );
  const projectedPeriodOverageCost =
    refillPricePerM * Math.ceil(projectedPeriodOverageTokens / 1000000);
  const includedUsedTokens = Math.max(
    0,
    Math.min(currentUsageTokens, includedAllowanceTokens)
  );
  const refillUsedTokens = Math.max(
    0,
    Math.min(
      currentUsageTokens - includedAllowanceTokens,
      refillAllowanceTokens
    )
  );
  const meterCapacityTokens = Math.max(
    1,
    totalAllowanceTokens,
    currentUsageTokens,
    projectedPeriodUsageTokens
  );
  const includedAllowanceWidthPct = Math.min(
    100,
    (includedAllowanceTokens / meterCapacityTokens) * 100
  );
  const refillAllowanceWidthPct = Math.min(
    100 - includedAllowanceWidthPct,
    (refillAllowanceTokens / meterCapacityTokens) * 100
  );
  const includedUsedWidthPct = Math.min(
    includedAllowanceWidthPct,
    (includedUsedTokens / meterCapacityTokens) * 100
  );
  const refillUsedWidthPct = Math.min(
    refillAllowanceWidthPct,
    (refillUsedTokens / meterCapacityTokens) * 100
  );
  const projectedUsageWidthPct = Math.min(
    100,
    Math.max(0, (projectedPeriodUsageTokens / meterCapacityTokens) * 100)
  );
  const insightsUnlocked = subscriptionQuantity >= insightsMinQuantity;
  const modelOverridesUnlocked =
    subscriptionQuantity >= modelOverridesMinQuantity;
  const aiInsightsUnlocked = subscriptionQuantity >= aiInsightsMinQuantity;
  const modelOverridesLockedTooltip = `Upgrade to Growth (subscription quantity ${modelOverridesMinQuantity}+) to set per-route default AI models.`;
  const aiInsightsLockedTooltip = `AI insights unlock on Pro tiers (subscription quantity ${aiInsightsMinQuantity}+).`;
  const insightsLockedTooltip = `Insights & Settings unlock at higher tiers (subscription quantity ${insightsMinQuantity}+).`;
  const isDevEnv =
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_ENV === "development" ||
    window.location.hostname === "localhost" ||
    window.location.hostname.includes("-dev");
  const configuredEasyAIBaseUrl = String(
    process.env.REACT_APP_EASYAI_BASE_URL || ""
  )
    .trim()
    .replace(/\/+$/, "");
  const easyAIBaseUrl =
    configuredEasyAIBaseUrl ||
    (isDevEnv
      ? "https://easyai-dev.cloud.passiolife.com"
      : "https://easyai.passiolife.com");
  const easyAILoginUrl = `${easyAIBaseUrl}/login${
    easyAIOwnerEmail ? `?email=${encodeURIComponent(easyAIOwnerEmail)}` : ""
  }`;
  const [easyAIIframeUrl, setEasyAIIframeUrl] = useState(easyAILoginUrl);

  useEffect(() => {
    setEasyAIIframeUrl((prev) =>
      !prev || prev === "about:blank" ? easyAILoginUrl : prev
    );
  }, [easyAILoginUrl]);

  useEffect(() => {
    if (!SHOW_HUB_ADVANCED_TABS && activeTab !== "account") {
      setActiveTab("account");
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "insights" && !insightsUnlocked) {
      setActiveTab("account");
    }
  }, [activeTab, insightsUnlocked]);

  useEffect(() => {
    const syncTabFromUrl = () => setActiveTab(getRequestedDetailsTab());
    window.addEventListener("popstate", syncTabFromUrl);
    window.addEventListener("hashchange", syncTabFromUrl);
    return () => {
      window.removeEventListener("popstate", syncTabFromUrl);
      window.removeEventListener("hashchange", syncTabFromUrl);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const nextHash = `#${detailsTabToHash(activeTab)}`;
    if (params.get("tab") === activeTab && window.location.hash === nextHash)
      return;
    params.set("tab", activeTab);
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${nextSearch}${nextHash}`
    );
  }, [activeTab]);

  const getProduct = async () =>
    await axios.get(`/accounts/products/${productQuery}/detail`);
  const getProductUsageDetail = async () =>
    await axios.get(
      `/accounts/products/${productQuery}/usage/detail?groupMode=${usageDetailGroupMode[1]}`
    );
  const getEasyAISSOURL = async () =>
    await axios.get("/accounts/products/unified/easyai/sso-url?package=hub");

  const getUsageHistoryDetail = async () =>
    await axios.get(
      `/accounts/products/${productQuery}/usage/history?periodStart=${billingPeriods[selectedPeriodIndex].start}&periodEnd=${billingPeriods[selectedPeriodIndex].end}&outputMode=${historyOutputMode}`
    );

  const [usedPercentage, setUsedPercentage] = useState(0);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [periodStart, setPeriodStart] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [periodEnd, setPeriodEnd] = useState<number | null>(null);
  const [billingPeriods, setBillingPeriods] = useState<
    { start: number; end: number }[]
  >([]);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);

  const {
    isLoading: usageDetailLoading,
    isFetching: usageDetailFetching,
    // isPaused: usageDetailPaused,
    // refetch: refetchUsageDetail,
  } = useQuery(
    [
      "usageDetail",
      productQuery,
      userContext?.customerid,
      usageDetailGroupMode[1],
    ],
    getProductUsageDetail,
    {
      enabled: !!productQuery && !!userContext?.customerid,
      onError: (err: AxiosError) => {
        error(
          err?.response?.data?.cause ||
            "Could not fetch the products usage detail"
        );
        if (err?.response?.status === 403) {
          setUnauthorized(true);
        }
      },
      onSuccess: (res: AxiosResponse) => {
        setUsageDetail(res?.data);
      },
    }
  );

  const { isLoading: hisoryDataLoading, isFetching: historyDataFetching } =
    useQuery(
      [
        "usageHistory",
        productQuery,
        userContext?.customerid,
        selectedPeriodIndex,
        historyOutputMode,
      ],
      getUsageHistoryDetail,
      {
        enabled: usageView === "history" && billingPeriods.length > 0,
        onError: (err: AxiosError) => {
          error(err?.response?.data?.cause || "Could not fetch the products");
          if (err?.response?.status === 403) {
            setUnauthorized(true);
          }
        },
        onSuccess: (res: AxiosResponse) => {
          // res.data.usageInfo.units = res?.data?.usageInfo?.units * 10 || 0;
          setUsageHistoryForPeriod(res?.data);
        },
      }
    );

  const { data: easyAISSOResponse } = useQuery(
    ["easyAISSOURL", userContext?.customerid, productSubDetail?.isSubscribed],
    getEasyAISSOURL,
    {
      enabled:
        SHOW_HUB_ADVANCED_TABS &&
        activeTab === "easyai" &&
        !!productSubDetail?.isSubscribed &&
        !!userContext?.customerid,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      retry: false,
      onError: () => {
        // Fall back to standard login URL without surfacing a user-facing error.
      },
    }
  );

  const easyAISSOData: EasyAISSOResponse | undefined = easyAISSOResponse?.data;
  const easyAISignInUrl =
    easyAISSOData?.ssoEnabled && easyAISSOData?.ssoUrl
      ? easyAISSOData.ssoUrl
      : easyAILoginUrl;

  const resolveFreshEasyAISignInURL = async (): Promise<string> => {
    let nextURL = easyAISignInUrl;
    try {
      const res = await getEasyAISSOURL();
      const freshData: EasyAISSOResponse | undefined = res?.data;
      if (freshData?.ssoEnabled && freshData?.ssoUrl) {
        nextURL = freshData.ssoUrl;
      } else if (freshData?.loginUrl) {
        nextURL = freshData.loginUrl;
      }
    } catch {
      // Fall back to current launch URL if fresh token retrieval fails.
    }
    return nextURL;
  };

  const launchEasyAIInIframe = async () => {
    const nextURL = await resolveFreshEasyAISignInURL();
    setEasyAIIframeUrl("about:blank");
    window.requestAnimationFrame(() => setEasyAIIframeUrl(nextURL));
  };

  const openEasyAIInNewTab = async () => {
    const nextURL = await resolveFreshEasyAISignInURL();
    window.open(nextURL, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!SHOW_HUB_ADVANCED_TABS) {
      return;
    }
    if (activeTab !== "easyai") {
      setEasyAIAutoSignInTriggered(false);
      return;
    }
    if (
      easyAIAutoSignInTriggered ||
      !productSubDetail?.isSubscribed ||
      !userContext?.customerid
    ) {
      return;
    }
    setEasyAIAutoSignInTriggered(true);
    launchEasyAIInIframe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    easyAIAutoSignInTriggered,
    productSubDetail?.isSubscribed,
    userContext?.customerid,
  ]);

  const { isLoading, isFetching, refetch } = useQuery(
    ["product", productQuery, userContext?.customerid],
    getProduct,
    {
      enabled: !!productQuery && !!userContext?.customerid,
      onError: (err: AxiosError) => {
        if (err?.response?.status === 404) {
          setHubSubscriptionUnavailable(true);
          return;
        }
        error(err?.response?.data?.cause || "Could not fetch the products");
        if (err?.response?.status === 403) {
          setUnauthorized(true);
        }
      },
      onSuccess: (res: AxiosResponse) => {
        setHubSubscriptionUnavailable(false);
        // res.data.usageInfo.units = res?.data?.usageInfo?.units * 10 || 0;
        setProductSubDetail(res?.data);
        const usageQuota = getUsageQuota(res?.data?.usageInfo);
        setUsedPercentage(
          usageQuota > 0
            ? ((res?.data?.usageInfo?.units || 0) / usageQuota) * 100
            : 0
        );
        setProductState((prev) => ({
          ...prev,
          isSubscribed: res.data?.isSubscribed,
          trialEnd: res.data?.trialEnd ? new Date(res.data?.trialEnd) : null,
          billingDate: new Date(res.data?.billingDate),
          licenseKey: res.data?.licenseKey,
          usageInfo: res.data?.usageInfo,
          tiers: res.data?.tiers || [],
        }));
        // Set period start and end
        const currentPeriodStart =
          res.data?.stripeSubscription?.current_period_start;
        const currentPeriodEnd =
          res.data?.stripeSubscription?.current_period_end;
        setPeriodStart(currentPeriodStart);
        setPeriodEnd(currentPeriodEnd);

        // Calculate and set billing periods
        if (currentPeriodStart && currentPeriodEnd) {
          const periods = [];
          const periodLength = currentPeriodEnd - currentPeriodStart;
          const subscriptionStart = res.data.stripeSubscription.start_date; // Assuming this is the subscription start date

          for (let i = 0; ; i++) {
            const start = currentPeriodStart - i * periodLength;
            const end = currentPeriodEnd - i * periodLength;
            if (start < subscriptionStart) break;
            periods.push({ start, end });
          }
          setBillingPeriods(periods);
        }
      },
    }
  );

  async function copyTextToClipboard(text: string) {
    return await navigator.clipboard.writeText(text);
  }

  const handleCopyClick = () => {
    copyTextToClipboard(productState?.licenseKey ?? "").then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    });
  };

  const handleActivateEasyAI = async () => {
    setIsActivatingEasyAI(true);
    setEasyAIActivationMessage("");
    try {
      const res = await axios.post(
        "/accounts/products/unified/easyai/activate"
      );
      if (res?.data?.result?.status === "ok") {
        setEasyAIActivationMessage(
          "Setup email sent. Open Easy AI and use Forgot password if needed."
        );
      } else {
        const fallback =
          res?.data?.result?.response_body || "Activation failed";
        setEasyAIActivationMessage(fallback);
        error(fallback);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.cause ||
        err?.response?.data?.message ||
        err?.message ||
        "Activation failed";
      setEasyAIActivationMessage(msg);
      error(msg);
    } finally {
      setIsActivatingEasyAI(false);
    }
  };

  // Google Adds Event Tracking
  useEffect(() => {
    // Function to get URL parameter
    const getQueryParam = (param: string) => {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(param);
    };
    const encodedData = getQueryParam("event");
    if (encodedData) {
      const decodedString = atob(encodedData);
      try {
        const obj = JSON.parse(decodedString);
        ReactGA.event(obj);
        // eslint-disable-next-line no-empty
      } catch (e) {}
    }
  }, []);

  const handleDownloadCSV = () => {
    const blob = new Blob([usageHistoryForPeriod?.usageCSV || ""], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const filename =
      historyOutputMode === "grouped"
        ? "usage_report_grouped.csv"
        : "usage_report_raw.csv";
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (unauthorized) {
    return <Navigate to="/access-restricted" />;
  }
  if (hubSubscriptionUnavailable) {
    return <Navigate to={`/details?product=${NUTRITION_PLATFORM_SLUG}`} />;
  }

  // TODO: DVS: Fix
  // if (!isFetching && !isLoading && !isPaused && !productState.isSubscribed) {
  //   return <Navigate to="/" />;
  // }
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-bold inter-bold text-md md:text-lg xl:text-3xl">
          Passio Hub Product Details
        </h2>
      </div>
      <div className="mb-6">
        <div className="flex items-end gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("account")}
            className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
              activeTab === "account"
                ? "bg-white text-indigo-700 border-gray-200"
                : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
            }`}
          >
            Account Info
          </button>
          {SHOW_HUB_ADVANCED_TABS && (
            <button
              onClick={() => setActiveTab("easyai")}
              className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 relative ${
                activeTab === "easyai"
                  ? "bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200"
                  : "bg-gradient-to-r from-gray-100 to-indigo-50 text-gray-700 border-transparent hover:text-indigo-600 hover:border-indigo-100"
              }`}
            >
              <span className="flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-yellow-500" />
                Easy AI
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse shadow-lg">
                  NEW
                </span>
              </span>
            </button>
          )}
          {SHOW_HUB_ADVANCED_TABS && (
            <span title={!insightsUnlocked ? insightsLockedTooltip : undefined}>
              <button
                onClick={() => {
                  if (!insightsUnlocked) return;
                  setActiveTab("insights");
                }}
                className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
                  activeTab === "insights"
                    ? "bg-white text-indigo-700 border-gray-200"
                    : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
                } ${
                  insightsUnlocked
                    ? ""
                    : "cursor-not-allowed text-gray-400 hover:text-gray-400"
                }`}
                aria-disabled={!insightsUnlocked}
              >
                <span className="inline-flex items-center gap-2">
                  Insights & Settings
                  {!insightsUnlocked && (
                    <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">
                      Locked
                    </span>
                  )}
                </span>
              </button>
            </span>
          )}
        </div>
      </div>
      <div
        className={`lg:grid-cols-2 gap-8 auto-rows-auto ${
          activeTab === "account" ? "grid" : "hidden"
        }`}
      >
        <Card className="sm:p-6">
          <div className="flex justify-between">
            <div className="flex flex-col">
              <p className="font-medium">Current Period</p>
              <span className="text-sm text-gray-500">
                {new Date(
                  productSubDetail?.stripeSubscription?.current_period_start *
                    1000
                )?.toLocaleDateString("default", {
                  month: "2-digit",
                  year: "2-digit",
                  day: "2-digit",
                })}
                {"  -  "}
                {new Date(
                  productSubDetail?.stripeSubscription?.current_period_end *
                    1000
                )?.toLocaleDateString("default", {
                  month: "2-digit",
                  year: "2-digit",
                  day: "2-digit",
                })}
              </span>
            </div>
            {!isFetching && !isLoading ? (
              productState.trialEnd ? (
                <Badge isWarning>
                  <ClockIcon
                    className="h-5 w-5 flex-shrink-0 mr-2"
                    aria-hidden="true"
                  />
                  Free Trial Ends on{" "}
                  {productState.trialEnd.toLocaleDateString()}
                </Badge>
              ) : (
                <Badge>
                  <ClockIcon
                    className="h-5 w-5 flex-shrink-0 mr-2"
                    aria-hidden="true"
                  />
                  Billed Next on {productState.billingDate.toLocaleDateString()}
                </Badge>
              )
            ) : (
              <></>
            )}
          </div>
          <div className="flex flex-col gap-4 mt-4">
            <div className="flex flex-col justify-center items-center w-full">
              <div className="flex flex-col items-center w-full mb-2">
                {/* <span className="text-sm text-gray-500 text-center mb-1 font-medium">
                  Current Usage
                </span> */}
                <div className="flex space-x-2 mb-8">
                  <button
                    onClick={() => setUsageView("basic")}
                    className={`px-3 py-1 text-sm font-medium rounded-full ${
                      usageView === "basic"
                        ? "bg-indigo-600 text-white"
                        : "bg-indigo-100 text-indigo-700 hover:bg-gray-300"
                    }`}
                  >
                    Basic
                  </button>
                  <button
                    onClick={() => setUsageView("graph")}
                    className={`px-3 py-1 text-sm font-medium rounded-full ${
                      usageView === "graph"
                        ? "bg-indigo-600 text-white"
                        : "bg-indigo-100 text-indigo-700 hover:bg-gray-300"
                    }`}
                  >
                    Graph
                  </button>
                  <button
                    onClick={() => setUsageView("history")}
                    className={`px-3 py-1 text-sm font-medium rounded-full ${
                      usageView === "history"
                        ? "bg-indigo-600 text-white"
                        : "bg-indigo-100 text-indigo-700 hover:bg-gray-300"
                    }`}
                  >
                    History
                  </button>
                </div>

                {isLoading || isFetching ? (
                  <Spinner className="!w-[2rem]" />
                ) : productSubDetail?.usageInfo?.units !== undefined ? (
                  <>
                    {usageView === "basic" && (
                      <>
                        <div className="text-passio-indigo text-md font-bold">
                          {convertToNumLetter(productSubDetail.usageInfo.units)}{" "}
                          /{" "}
                          {convertToNumLetter(
                            productSubDetail.usageInfo.unitsQuota
                          )}
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-6 relative overflow-hidden border border-gray-200">
                          <div
                            className="absolute inset-y-0 left-0 bg-indigo-100"
                            style={{ width: `${includedAllowanceWidthPct}%` }}
                          />
                          <div
                            className="absolute inset-y-0 bg-emerald-100"
                            style={{
                              left: `${includedAllowanceWidthPct}%`,
                              width: `${refillAllowanceWidthPct}%`,
                            }}
                          />
                          <div
                            className="absolute inset-y-0 left-0 bg-indigo-600"
                            style={{ width: `${includedUsedWidthPct}%` }}
                          />
                          {refillUsedWidthPct > 0 && (
                            <div
                              className="absolute inset-y-0 bg-emerald-600"
                              style={{
                                left: `${includedAllowanceWidthPct}%`,
                                width: `${refillUsedWidthPct}%`,
                              }}
                            />
                          )}
                          <div
                            className="absolute inset-y-0 border-l-2 border-amber-500"
                            style={{
                              left: `calc(${projectedUsageWidthPct}% - 1px)`,
                            }}
                          />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2.5 w-2.5 rounded-full bg-indigo-600" />
                            Used included
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
                            Used refill
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2.5 w-2.5 rounded-full bg-indigo-100 border border-indigo-200" />
                            Included allowance
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-100 border border-emerald-200" />
                            Refill allowance
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-3 w-0 border-l-2 border-amber-500" />
                            Projected usage
                          </span>
                        </div>
                        <div className="flex justify-between w-full text-indigo-600">
                          <span className="text-sm font-medium">
                            {usedPercentage.toFixed(1)}% Used {" ("}
                            {predictedPercentage.toFixed(1)}% Projected for
                            period{")"}
                          </span>
                          {usedPercentage < 100 && (
                            <span className="text-sm font-medium">
                              {(100 - usedPercentage).toFixed(1)}% Available
                            </span>
                          )}
                        </div>
                        {effectiveUsageQuota > 0 && (
                          <div className="w-full mt-2 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                            <p>
                              Included plan allowance:{" "}
                              {convertToNumLetter(includedAllowanceTokens)}
                            </p>
                            <p>
                              Refill allowance added:{" "}
                              {convertToNumLetter(refillAllowanceTokens)}
                            </p>
                            <p>
                              Current usage:{" "}
                              {convertToNumLetter(
                                Math.round(currentUsageTokens)
                              )}
                            </p>
                            <p>
                              Usage from included tokens:{" "}
                              {convertToNumLetter(
                                Math.round(includedUsedTokens)
                              )}
                            </p>
                            <p>
                              Usage from refill tokens:{" "}
                              {convertToNumLetter(Math.round(refillUsedTokens))}
                            </p>
                            <p>
                              Projected usage this period:{" "}
                              {convertToNumLetter(
                                Math.round(projectedPeriodUsageTokens)
                              )}
                            </p>
                            <p className="font-semibold">
                              Projected billed overage:{" "}
                              {convertToNumLetter(
                                Math.round(projectedPeriodOverageTokens)
                              )}{" "}
                              (
                              {projectedPeriodOverageTokens > 0
                                ? projectedPeriodOverageCost.toLocaleString(
                                    "en-US",
                                    {
                                      currency: "USD",
                                      style: "currency",
                                    }
                                  )
                                : "$0.00"}
                              )
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    {usageView === "history" && (
                      <>
                        {!historyDataFetching && !hisoryDataLoading ? (
                          <div className="space-y-4">
                            {/* Billing period navigation */}
                            <div>
                              <label
                                htmlFor="billing-period"
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Billing Period
                              </label>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    setSelectedPeriodIndex((prev) =>
                                      Math.min(
                                        prev + 1,
                                        billingPeriods.length - 1
                                      )
                                    )
                                  }
                                  disabled={
                                    selectedPeriodIndex >=
                                    billingPeriods.length - 1
                                  }
                                  className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Previous period"
                                >
                                  <ChevronLeftIcon className="w-4 h-4" />
                                </button>
                                <select
                                  id="billing-period"
                                  className="flex-1 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                  value={selectedPeriodIndex}
                                  onChange={(e) =>
                                    setSelectedPeriodIndex(
                                      parseInt(e.target.value)
                                    )
                                  }
                                >
                                  {billingPeriods.map((period, index) => (
                                    <option key={index} value={index}>
                                      {new Date(
                                        period.start * 1000
                                      ).toLocaleDateString()}{" "}
                                      -{" "}
                                      {new Date(
                                        period.end * 1000
                                      ).toLocaleDateString()}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={() =>
                                    setSelectedPeriodIndex((prev) =>
                                      Math.max(prev - 1, 0)
                                    )
                                  }
                                  disabled={selectedPeriodIndex <= 0}
                                  className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Next period"
                                >
                                  <ChevronRightIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>

                            {/* Output mode toggle + download */}
                            <div className="flex items-center justify-between">
                              <div className="inline-flex rounded-md shadow-sm">
                                <button
                                  onClick={() =>
                                    setHistoryOutputMode("grouped")
                                  }
                                  className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                                    historyOutputMode === "grouped"
                                      ? "bg-indigo-600 text-white border-indigo-600"
                                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                  }`}
                                >
                                  Grouped Output
                                </button>
                                <button
                                  onClick={() => setHistoryOutputMode("raw")}
                                  className={`px-3 py-1.5 text-sm font-medium rounded-r-md border-t border-b border-r ${
                                    historyOutputMode === "raw"
                                      ? "bg-indigo-600 text-white border-indigo-600"
                                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                  }`}
                                >
                                  Raw Output
                                </button>
                              </div>
                              <Button
                                onClick={handleDownloadCSV}
                                className="whitespace-nowrap"
                              >
                                <ArrowDownTrayIcon className="w-4 h-4 mr-1.5 inline" />
                                Download CSV
                              </Button>
                            </div>
                            {historyOutputMode === "raw" && (
                              <p className="text-xs text-amber-600">
                                Raw output includes every individual API call.
                                For high-usage periods this may be a large file.
                              </p>
                            )}

                            {/* Invoice Links */}
                            {usageHistoryForPeriod?.invoiceLinks &&
                              usageHistoryForPeriod.invoiceLinks.length > 0 && (
                                <div>
                                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                                    Invoice Downloads
                                  </h3>
                                  <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                                    {usageHistoryForPeriod.invoiceLinks.map(
                                      (link, index) => (
                                        <li
                                          key={`inv-${index}`}
                                          className="px-4 py-2 hover:bg-gray-50"
                                        >
                                          <a
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm"
                                          >
                                            <ArrowDownTrayIcon className="pr-2 w-5 h-5" />
                                            Download Period Invoice {index + 1}
                                          </a>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}
                          </div>
                        ) : (
                          <Spinner className="!w-[2rem]"></Spinner>
                        )}
                      </>
                    )}
                    {usageView === "graph" && (
                      <div className="w-full mt-4 items-center align-middle content-center">
                        <Plot
                          data={[
                            {
                              type: "bar" as const,
                              x: [
                                "Used",
                                "Remaining",
                                "Avg/Day",
                                "Projected Usage",
                                "Projected Overage",
                              ],

                              y: [
                                productSubDetail.usageInfo.units,
                                productSubDetail.usageInfo.unitsQuota -
                                  productSubDetail.usageInfo.units,
                                productSubDetail.usageInfo.units /
                                  ((new Date().getTime() -
                                    new Date(
                                      productSubDetail.stripeSubscription
                                        .current_period_start * 1000
                                    ).getTime()) /
                                    (1000 * 60 * 60 * 24)),
                                (predictedPercentage *
                                  productSubDetail.usageInfo.unitsQuota) /
                                  100,
                                Math.max(
                                  0,
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100 -
                                    productSubDetail.usageInfo.unitsQuota
                                ),
                              ],
                              text: [
                                convertToNumLetter(
                                  productSubDetail.usageInfo.units
                                ),
                                convertToNumLetter(
                                  productSubDetail.usageInfo.unitsQuota -
                                    productSubDetail.usageInfo.units
                                ),
                                convertToNumLetter(
                                  productSubDetail.usageInfo.units /
                                    ((new Date().getTime() -
                                      new Date(
                                        productSubDetail.stripeSubscription
                                          .current_period_start * 1000
                                      ).getTime()) /
                                      (1000 * 60 * 60 * 24))
                                ),
                                convertToNumLetter(
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100
                                ),
                                convertToNumLetter(
                                  Math.max(
                                    0,
                                    (predictedPercentage *
                                      productSubDetail.usageInfo.unitsQuota) /
                                      100 -
                                      productSubDetail.usageInfo.unitsQuota
                                  )
                                ),
                              ],
                              textposition: "auto" as const,
                              hoverinfo: "text" as const,
                              marker: {
                                color: [
                                  "#4F46E5",
                                  "#818CF8",
                                  "#A5B4FC",
                                  "#C7D2FE",
                                  "#a42820",
                                ],
                              },
                              yaxis: "y" as const,
                            },
                            {
                              type: "bar" as const,
                              x: ["Days Left"],
                              y: [
                                Math.ceil(
                                  (new Date(
                                    productSubDetail.stripeSubscription
                                      .current_period_end * 1000
                                  ).getTime() -
                                    new Date().getTime()) /
                                    (1000 * 60 * 60 * 24)
                                ),
                              ],
                              text: [
                                Math.ceil(
                                  (new Date(
                                    productSubDetail.stripeSubscription
                                      .current_period_end * 1000
                                  ).getTime() -
                                    new Date().getTime()) /
                                    (1000 * 60 * 60 * 24)
                                ).toString() + " days",
                              ],
                              textposition: "auto" as const,
                              hoverinfo: "text" as const,
                              marker: {
                                color: "#10B981",
                              },
                              yaxis: "y2" as const,
                            },
                          ]}
                          layout={{
                            title: "Token Usage Overview",
                            font: { size: 10 },
                            autosize: true,
                            height: 300,
                            margin: { l: 40, r: 40, t: 40, b: 80 },
                            bargap: 0.15,
                            yaxis: {
                              title: "Tokens",
                              side: "left" as const,
                              range: [
                                0,
                                Math.max(
                                  productSubDetail.usageInfo.unitsQuota,
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100
                                ),
                              ],
                            },
                            yaxis2: {
                              title: "Billing Period Days",
                              side: "right" as const,
                              overlaying: "y" as const,
                              rangemode: "tozero" as const,
                              range: [
                                0,
                                Math.max(
                                  31,
                                  Math.ceil(
                                    (new Date(
                                      productSubDetail.stripeSubscription
                                        .current_period_end * 1000
                                    ).getTime() -
                                      new Date().getTime()) /
                                      (1000 * 60 * 60 * 24)
                                  )
                                ),
                              ],
                              dtick: 5,
                            },
                            showlegend: false,
                          }}
                          useResizeHandler={true}
                          // style={{ width: "450px", height: "220px" }}
                          style={{ width: "auto", height: "220px" }}
                          config={{
                            displayModeBar: false,
                            responsive: true,
                          }}
                        />
                      </div>
                    )}
                    <div className="text-sm text-gray-500 mt-2 mb-2 w-full">
                      {projectedPeriodOverageTokens > 0 && (
                        <div className="mt-2 p-4 bg-red-100 rounded-md text-red-600">
                          <p className="">
                            Warning: You are projected to exceed your included
                            tokens this period.
                          </p>
                          <p>
                            {productSubDetail.stripeSubscription.metadata
                              .refillBehaviour === "auto"
                              ? `Auto-refill will purchase more tokens at ${CREDIT_REFILL_PRICE_DISPLAY} per million.`
                              : " Consider enabling auto-refill or manually purchasing more tokens to avoid service interruption."}
                          </p>
                          <p className="pt-2 font-bold">
                            {`Projected Billed Overage: ${projectedPeriodOverageCost.toLocaleString(
                              "en-US",
                              {
                                currency: "USD",
                                style: "currency",
                              }
                            )}`}
                          </p>
                          <p className="pt-1 text-xs">
                            Included tokens are applied first. This amount only
                            reflects projected usage above your included quota.
                          </p>
                        </div>
                      )}
                    </div>
                    <span className="text-xs mt-2 text-gray-800">
                      *Usage and predictions in this dashboard may take a few
                      minutes to reflect the latest data. Your API client
                      responses will be up-to-date.
                    </span>
                  </>
                ) : (
                  "-"
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card className="sm:p-6 row-span-2">
          <div className="flex flex-col">
            <p className="font-medium">API Key & Info</p>
          </div>

          <div className="mt-6">
            <dt className="text-sm font-medium text-gray-500">API Key</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <ul
                role="list"
                className="divide-y divide-gray-200 rounded-md border border-gray-200"
              >
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <KeyIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      {hideKey
                        ? Array.from({
                            length: productState?.licenseKey?.length || 0,
                          }).map((_, index) => (
                            <span key={index}>•</span> // Customize dot size with Tailwind classes
                          ))
                        : productState?.licenseKey ?? ""}
                    </span>
                  </div>
                  <div className="ml-4 flex flex-row flex-shrink items-center space-x-2">
                    {isCopied ? (
                      <ClipboardDocumentCheckIcon
                        className="h-5 w-5 flex-shrink-0 text-green-700"
                        aria-hidden="true"
                      />
                    ) : (
                      <DocumentDuplicateIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        aria-hidden="true"
                        onClick={handleCopyClick}
                      />
                    )}
                    {hideKey ? (
                      <EyeSlashIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        aria-hidden="true"
                        onClick={() => setHideKey(!hideKey)}
                      ></EyeSlashIcon>
                    ) : (
                      <EyeIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        aria-hidden="true"
                        onClick={() => setHideKey(!hideKey)}
                      ></EyeIcon>
                    )}
                  </div>
                </li>
              </ul>
              <p className="text-gray-500 text-sm ml-4 mt-1">
                * Keep your API Key safe - see the best practices in the
                documentation.
              </p>
            </dd>
          </div>

          <div className="pt-8">
            <dd className="mt-1 text-sm text-gray-900">
              <ul
                role="list"
                className="divide-y divide-gray-200 rounded-md border border-gray-200"
              >
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <ArrowTopRightOnSquareIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      <a
                        href={"https://passio.gitbook.io/nutrition-ai"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        Documentation
                      </a>
                    </span>
                  </div>
                </li>
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <ArrowTopRightOnSquareIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      <a
                        href={
                          "https://passio.gitbook.io/nutrition-ai/rest-api/api-overview/authorization-setup"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        Authorize With Your API Key
                      </a>
                    </span>
                  </div>
                </li>
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <ArrowTopRightOnSquareIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      <a
                        href={"https://api.passiolife.com/v2/products/docs"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        OpenAPI Docs
                      </a>
                    </span>
                  </div>
                </li>
              </ul>
            </dd>
          </div>

          {/* Integration Help Section */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  Need Help with Integration?
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Chat with our AI assistant for help with REST API
                  authorization, usage, code examples, and more.
                </p>
                <button
                  onClick={() => setIsChatbotOpen(true)}
                  className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                  Open Integration Assistant
                </button>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <dt className="text-sm font-medium text-gray-500">Other Links</dt>
            <dd className="mt-2 text-sm text-gray-900">
              <ul
                role="list"
                className="divide-y divide-gray-200 rounded-md border border-gray-200"
              >
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <ArrowTopRightOnSquareIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      <Link
                        to="/tools/mealplans"
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        Meal Planning Tool
                      </Link>
                    </span>
                  </div>
                </li>
              </ul>
            </dd>
          </div>
        </Card>

        {/* <Card> */}
        <div className="flex-grow">
          {(!!isLoading && !!isFetching) || !!productSubDetail ? (
            <RefillSettingsCard
              subscriptionMetadata={ConvertToSubscriptionMetadata(
                productSubDetail?.stripeSubscription?.metadata ||
                  ({} as SubscriptionMetadata)
              )}
              onRefill={async () => {
                await axios.post(
                  "/accounts/products/nutrition-hub/subscription/refill"
                );
                await new Promise((resolve) => setTimeout(resolve, 2000));
                // Refresh data in background - don't let refetch errors affect the success status
                refetch().catch(() => {});
              }}
              onSave={async (newMetadata: SubscriptionMetadata) => {
                await axios.put(
                  "/accounts/products/nutrition-hub/subscription",
                  newMetadata
                );
                // Refresh data in background - don't let refetch errors affect the success status
                refetch().catch(() => {});
              }}
            />
          ) : (
            <Spinner></Spinner>
          )}
        </div>
        {/* </Card> */}
        <div className="w-full col-span-2 h-min">
          {!usageDetailFetching && !usageDetailLoading && usageDetail ? (
            <UsageDetailCard
              usageDetail={usageDetail}
              usageDetailGroupMode={usageDetailGroupMode}
              setUsageDetailGroupMode={setUsageDetailGroupMode}
              groupModes={groupModes}
            />
          ) : (
            <Card>
              <Spinner></Spinner>
            </Card>
          )}
        </div>

        {/* <div className="w-full col-span-2">
          <PriceTablePassioHub
            isLoading={isLoading || isFetching}
            tiers={productState.tiers}
            showDescription={true}
          />
        </div> */}
        <Card className="sm:p-6">
          <div>
            <p className="font-medium">Support & Documentation</p>
            <span className="text-sm text-gray-500">
              Links for additional support
            </span>
          </div>

          <div className="mt-4">
            <dt className="text-sm font-medium text-gray-500">Documentation</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <a
                href="https://passio.gitbook.io/nutrition-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                https://passio.gitbook.io/nutrition-ai
              </a>
            </dd>
          </div>
          <div className="mt-4">
            <dt className="text-sm font-medium text-gray-500">Email Support</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <a
                href="mailto:support@passiolife.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                support@passiolife.com
              </a>
            </dd>
          </div>
          <div className="mt-4">
            <dt className="text-sm font-medium text-gray-500">Slack Support</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <a
                href="https://passio-community.slack.com/channels/help"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                https://passio-community.slack.com/channels/help
              </a>
            </dd>
          </div>
          <div className="mt-4">
            <dt className="text-sm font-medium text-gray-500">Roadmap</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <a
                href="https://portal.productboard.com/passio/1-nutrition-ai/tabs/1-recently-launched"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                https://portal.productboard.com/passio/1-nutrition-ai/tabs/1-recently-launched
              </a>
            </dd>
          </div>
        </Card>
        <SlackSignUp />
      </div>
      {SHOW_HUB_ADVANCED_TABS && (
        <div className={activeTab === "easyai" ? "block" : "hidden"}>
          <Card className="sm:p-0 overflow-hidden">
            <div className="mx-4 my-4 rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
              <button
                onClick={() => setShowEasyAISignIn((prev) => !prev)}
                className="w-full p-3 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <InformationCircleIcon className="h-5 w-5 text-amber-600" />
                  <span className="font-semibold text-amber-900">
                    Easy AI Sign-in Info
                  </span>
                </div>
                <span className="text-sm font-semibold text-amber-700">
                  {showEasyAISignIn ? "Hide" : "Show"}
                </span>
              </button>
              {showEasyAISignIn && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-amber-800 mb-3">
                    Passio SSO runs automatically when you open this tab.
                  </p>
                  <p className="text-sm text-amber-800 mb-3">
                    Use the same email as your current account:{" "}
                    <span className="font-mono bg-amber-100 px-1 rounded">
                      {easyAIOwnerEmail || "your email"}
                    </span>
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={openEasyAIInNewTab}
                      className="inline-flex items-center rounded-md border border-indigo-300 bg-indigo-100/70 px-3 py-2 text-sm font-semibold text-indigo-800 shadow-sm transition hover:bg-indigo-100"
                    >
                      Open in new tab
                      <ArrowTopRightOnSquareIcon className="ml-1.5 h-4 w-4" />
                    </button>
                    {isEasyAIEligiblePlan && (
                      <button
                        onClick={handleActivateEasyAI}
                        disabled={isActivatingEasyAI}
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-md hover:bg-indigo-700 disabled:opacity-60"
                      >
                        {isActivatingEasyAI
                          ? "Sending..."
                          : "Resend Setup Email"}
                      </button>
                    )}
                  </div>
                  {easyAIActivationMessage && (
                    <p className="mt-2 text-sm text-indigo-700">
                      {easyAIActivationMessage}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="h-[92vh] min-h-[940px]">
              <iframe
                src={easyAIIframeUrl}
                className="w-full h-full border-0"
                title="EasyAI"
              />
            </div>
          </Card>
        </div>
      )}
      {SHOW_HUB_ADVANCED_TABS && (
        <div className={activeTab === "insights" ? "block" : "hidden"}>
          <RouteInsights
            onOpenEasyAI={() => setActiveTab("easyai")}
            onOpenUpgrade={() =>
              navigate(
                `/manage-subscription?product=${productQuery || "unified"}`
              )
            }
            packageMode="hub"
            includedTokensPerPeriod={totalAllowanceTokens}
            authoritativeUsageTokens={Number(
              productSubDetail?.usageInfo?.units ||
                productState?.usageInfo?.units ||
                0
            )}
            authoritativeProjectedPeriodTokens={projectedPeriodUsageTokens}
            modelOverridesUnlocked={modelOverridesUnlocked}
            modelOverridesLockedTooltip={modelOverridesLockedTooltip}
            aiInsightsUnlocked={aiInsightsUnlocked}
            aiInsightsLockedTooltip={aiInsightsLockedTooltip}
            subscriptionStartUnix={Number(
              productSubDetail?.stripeSubscription?.start_date || 0
            )}
            billingPeriodStartUnix={Number(
              productSubDetail?.stripeSubscription?.current_period_start || 0
            )}
            billingPeriodEndUnix={Number(
              productSubDetail?.stripeSubscription?.current_period_end || 0
            )}
          />
        </div>
      )}

      {/* Chatbot Overlay */}
      {isChatbotOpen && (
        <div className="fixed inset-y-0 right-0 w-[550px] bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              <h2 className="text-lg font-semibold">Integration Assistant</h2>
            </div>
            <button
              onClick={() => setIsChatbotOpen(false)}
              className="p-1 hover:bg-indigo-700 rounded-md transition-colors"
              aria-label="Close chat"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Iframe Container */}
          <div className="flex-1 overflow-hidden">
            <iframe
              src="https://easyai.cloud-prod.passiolife.com/embed/chatbot?chatbotId=6453e3ba-df9a-4d1e-8a05-2800bcdb0997&apiKey=ZT3ift4W1-d7IoOz_x3MQMng6yZocy-2UAXaz62AtEY&customerTag=easyaitest"
              className="w-full h-full border-0"
              title="Integration Assistant Chatbot"
            />
          </div>

          {/* EasyAI Promotional Section */}
          <div className="px-4 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-t border-gray-200">
            <div className="text-center mb-3">
              <p className="text-sm text-gray-700 mb-2 font-medium">
                🚀 This chatbot was created in seconds using
              </p>
              <a
                href="https://easyai.cloud-prod.passiolife.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Passio EasyAI
                <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4" />
              </a>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Create your own AI chatbots with custom knowledge from any file
              type, custom functions, and image understanding. All API routes
              are hosted and production-ready within 30 seconds. AI-guided setup
              with the latest models, adjustable for cost and quality.
            </p>
          </div>
        </div>
      )}

      {/* Overlay backdrop for mobile/smaller screens */}
      {isChatbotOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setIsChatbotOpen(false)}
        />
      )}
    </>
  );
};

export default DetailsPassioHub;
